'use client';
import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowUpCircleIcon } from "@heroicons/react/24/outline";
import { useAuthUserContext } from "@/contexts/authUserContext";
import { trackClick } from "@/lib/analytics";

interface AddCommentsProps {
  onAddComment?: (comment: string) => Promise<void> | void;
  loading?: boolean;
}

const AddComments: React.FC<AddCommentsProps> = ({ onAddComment, loading = false }) => {
  const {isAuthenticated, user} = useAuthUserContext();
  const router = useRouter();
  const [input, setInput] = useState("");

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    if (onAddComment) {
      trackClick('add_comment_submit');
      let cacheInput = input.trim();
      try {
        setInput("");
        await onAddComment(input.trim());
      } catch (error) {
        console.error("Failed to add comment:", error);
        setInput(cacheInput); // Restore input if error occurs
      }
    }
  };

  const redirectToLogin = () => {
    trackClick('add_comment_login_redirect');
    router.push("/auth/login");
  }

  if (!user) {
    return (
      <div className="flex absolute bottom-0 left-0 right-0 items-center border-t border-t-light-gray px-4 py-2 bg-gray-50">
        <button
          className="bg-primary text-white font-medium w-80 h-10 rounded-full m-auto"
          onClick={redirectToLogin}
        >
          Login to Add Comments
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleAddComment}
      className="flex absolute bottom-0 left-0 right-0 items-center border-t border-t-light-gray px-4 py-2 bg-gray-50"
    >
      <input
        type="text"
        className="flex-1 rounded-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm"
        placeholder="Add comment..."
        value={input}
        onChange={(e) => setInput(e.target.value)}
        disabled={loading}
      />
      <button
        type="submit"
        className="text-primary disabled:text-gray-400"
        disabled={loading || !input.trim()}
      >
        <ArrowUpCircleIcon className="h-8 w-8 ml-1" />
      </button>
    </form>
  );
};

export default AddComments;